import { DocumentModel } from '@components/FileUpload';
import { Coordinate } from '@components/Map/Map';
import { MenuItemType } from '@components/Select';
import { AllBids } from '@store/capacityTools/types';
import { createAppStore } from '@store/createAppStore';
import { FeatureFlagCustomer } from '@typeDefinitions/featureFlagTypes';
import { IOrderViewModel } from '@typeDefinitions/orderTypes';
import { availableServices } from '@typeDefinitions/postingTypes';
import { LastUsedTopStopModel } from '@typeDefinitions/stopTypes';
import { createSelectMenuItemsFroObjectOfArrays } from '@utils/utils';
import cloneDeep from 'lodash/cloneDeep';
import {
  DEFAULT_ORDER_STATE,
  defaultFeatureFlagCustomerState,
} from '@common/order/constants';

import { defaultCustomerDetails } from './defaultVals';
import {
  CarrierState,
  CommentViewModel,
  Commodity,
  CustomerDetails,
  CustomerState,
  EdiLogState,
  EquipmentType,
  FeatureFlagCustomerState,
  ILoadTender,
  IManagePostLoad,
  IPostLoad,
  LoadSureState,
  LoadTenderState,
  OrderDocumentState,
  OrderState,
  Playlist,
  PostLoadState,
  TabType,
  UserData,
  ediLog,
} from './types';

const DEFAULT_TAB_STATE = {
  index: 0,
  type: TabType.load,
};

const initManagePostLoad: IManagePostLoad = {
  id: -1,
  loadId: -1,
  boardsPostedTo: [],
  origin: '',
  originCity: '',
  originState: '',
  originCountry: '',
  destination: '',
  destinationCity: '',
  destinationState: '',
  destinationCountry: '',
  pickUp: '',
  dropOff: '',
  price: null,
  equipment: '',
  comment: '',
  drops: 0,
  length: 0,
  weight: 0,
  servicesToPostTo: [],
  fullTruckload: false,
  mileage: 0,
  internalNote: '',
  errors: [],
};

const [loadSureStore, setLoadSureStore] = createAppStore<LoadSureState>(
  {
    cargoValue: null,
    equipment: null,
    commodity: null,
    descriptionOfGoodsInsured: null,
    isGap: false,
    isAllRisk: false,
    confirmation: false,
    allRiskInsuranceQuote: null,
    gapInsuranceQuote: null,
    allRiskToken: null,
    gapToken: null,
    insuredCompanyName: null,
    insuredCompanyPhone: null,
    insuredCompanyAddress1: null,
    insuredCompanyCity: null,
    insuredCompanyState: null,
    insuredCompanyZip: null,
    insuredCompanyCountry: null,
    customerContactEmail: null,
    loadId: null,
    otherCommodity: null,
    otherEquipment: null,
    isDirty: [],
    callback: null,
    successCallback: null,
  },
  'loadSureStore',
);

const initialOrderStateFactory = (): OrderState => {
  return cloneDeep({
    order: {
      ...(DEFAULT_ORDER_STATE as unknown as IOrderViewModel),
      loads: [],
    },
    orderFormError: null,
    orderFormErrorPerSection: {
      customerInformation: 0,
      loads: 0,
      instructionsAndCosts: 0,
    },
    orderFormSoftErrors: {},
    mappedErrorSection: null,
    loading: false,
    isSaving: false,
    isSubmittingPickup: false,
    isReadOnly: false,
    isError: false,
    activeTab: DEFAULT_TAB_STATE,
    lastStops: [],
    topStops: [],
    mode: '',
    mapCords: [
      {
        lat: 0,
        lng: 0,
        city: '',
        state: '',
        locationTime: '',
        trackingSource: '',
      },
    ],
    availableServices: [{ id: 0, name: '', quickPostInclude: false }],
    managePostLoad: { ...initManagePostLoad },
    nearbyCarriers: [],
    isCreate: false,
    isStopsEdited: false,
    orderTemplates: [],
    carrierContactLockdownEnabled: undefined,
    isTemplateMode: false,
    matched: false,
    //Falvey insurance error as vendor will always be one in this case adding a string
    vendorFalveyInsuranceError: '',
    copiedOrderIdUrl: '',
    copiedOrderId: '',
    orderIdUrl: undefined,
    quoteIdUrl: undefined, //this is the dbo.LTLQuote.Id
    quoteId: undefined, //this is the ltl.Quote.Id
    orderLockEnabled: undefined,
    forceUnlockOrderEnabled: undefined,
    isMobile: false,
    is1080p: true,
    viewedBy: [],
    resetDirty: false,
  });
};

const [orderStore, setOrderStore, resetOrderStore] = createAppStore<OrderState>(
  initialOrderStateFactory(),
  'orderStore',
);

export const customResetStore = (isCreate = true) => {
  const newState = initialOrderStateFactory();
  newState.isCreate = isCreate;
  newState.quoteId = undefined;
  newState.orderFormSoftErrors = {};
  setOrderStore(newState);
};

const [customerStore, setCustomerStore, resetCustomerStore] =
  createAppStore<CustomerState>(
    {
      // @ts-expect-error Ignore type mismatch.
      customer: defaultCustomerDetails,
      loading: false,
    },
    'customerStore',
  );

const [postLoadStore, setPostLoadStore] = createAppStore<PostLoadState>(
  {
    postLoad: {} as IPostLoad,
    isError: false,
    loading: true,
  },
  'postLoadStore',
);
const [orderDocumentStore, setOrderDocumentStore, resetOrderDocumentStore] =
  createAppStore<OrderDocumentState>(
    {
      documents: [] as DocumentModel[],
      loading: true,
    },
    'orderDocumentStore',
  );

const [ediLogsStore, setEdiLogsStore] = createAppStore<EdiLogState>(
  {
    ediLogs: [] as ediLog[],
    loading: false,
    isError: false,
  },
  'ediLogsStore',
);

const [loadTenderStore, setLoadTenderStore] = createAppStore<LoadTenderState>(
  {
    loadTender: {} as ILoadTender,
    loading: true,
    isError: false,
  },
  'loadTenderStore',
);

const [carrierStore, setCarrierStore, resetCarrierStore] =
  createAppStore<CarrierState>(
    {
      carrier: [],
      featureFlags: [],
      playlists: [],
      loading: false,
      isError: false,
    },
    'carrierStore',
  );

const [offerStore, setOfferStore, resetOfferStore] = createAppStore<AllBids>(
  {
    totalMileage: 0,
    loadNumber: '',
    loadStatus: '',
    stops: [],
    equipmentType: null,
    weight: null,
    commodity: null,
    temperature: null,
    maxRate: null,
    notes: null,
    teamRequired: false,
    hazmatRequired: false,
    tarpRequired: false,
    overDimension: false,
    rampsRequired: false,
    cargoValue: 0,
    orderId: '',
    bids: [],
    customerPayLineItems: [],
    datMedianRate: null,
    totalCustomerPay: 0,
  },
  'offerStore',
);

const [ediSettingsValues, setEdiSettingsValues] = createAppStore(
  {
    ediSettings: {},
    loading: false,
    isError: false,
  },
  'ediSettingsValues',
);

const [commodityCodes, setCommodityCodes] = createAppStore<Commodity[]>(
  [],
  'commodityCodes',
);
const [equipmentTypes, setEquipmentTypes] = createAppStore<EquipmentType[]>(
  [],
  'equipmentTypes',
);

const [eldAssetList, setEldAssetList] = createAppStore<MenuItemType[]>(
  [],
  'eldAssetList',
);

const [
  featureFlagGetCustomerFeatures,
  setFeatureFlagGetCustomerFeatures,
  resetFeatureFlagGetCustomerFeatures,
] = createAppStore<FeatureFlagCustomerState>(
  {
    featureFlagCustomer: { ...defaultFeatureFlagCustomerState },
    loading: false,
    isError: false,
  },
  'featureFlagGetCustomerFeatures',
);

export function setFeatureFlagGetCustomerFeaturesData(
  data: FeatureFlagCustomer,
) {
  setFeatureFlagGetCustomerFeatures('featureFlagCustomer', data);
}

export function setCopiedOrderId(copyOrderId: string) {
  setOrderStore('copiedOrderId', copyOrderId);
}

export function setOrderViewedBy(users: UserData[]) {
  setOrderStore('viewedBy', users);
}

export function setOrderLockedBy(user: UserData | undefined | null) {
  setOrderStore('lockedBy', user);
}

export function setFeatureFlagGetCustomerFeaturesLoading(loading: boolean) {
  setFeatureFlagGetCustomerFeatures('loading', loading);
}

export function setFeatureFlagGetCustomerFeaturesError(error: boolean) {
  setFeatureFlagGetCustomerFeatures('isError', error);
}

export function setEdiSettingsValuesData(data: Record<string, string[]>) {
  setEdiSettingsValues(
    'ediSettings',
    createSelectMenuItemsFroObjectOfArrays(data),
  );
}

export function setEdiSettingsValuesLoading(loading: boolean) {
  setEdiSettingsValues('loading', loading);
}

export function setEdiSettingsValuesError(error: boolean) {
  setEdiSettingsValues('isError', error);
}

// Function to set order data
export function setOrderData(data: IOrderViewModel) {
  setOrderStore('order', data);
}

export function setFlagComments(
  loadIdx: number,
  flagIdx: number,
  comments: CommentViewModel[],
) {
  setOrderStore(
    'order',
    'loads',
    loadIdx,
    'flags',
    flagIdx,
    'comments',
    comments,
  );
}

// Function to set loading state
export function setOrderLoading(loading: boolean) {
  setOrderStore('loading', loading);
}

export function setAvailableSevices(data: availableServices[]) {
  setOrderStore('availableServices', data);
}
export function setCustomerData(data: CustomerDetails) {
  setCustomerStore(() => ({
    customer: data,
    isLoading: false,
  }));
}

export function setManagePostLoadData(data: IManagePostLoad) {
  setOrderStore('managePostLoad', data);
}
export function setPostLoadData(data: IPostLoad) {
  setPostLoadStore(() => ({
    postLoad: data,
    isLoading: false,
  }));
}
export function setEdiLogsData(data: ediLog[]) {
  setEdiLogsStore(() => ({
    ediLogs: data,
    isLoading: false,
  }));
}

export function setCustomerLoading(loading: boolean) {
  setCustomerStore('loading', loading);
}

export function setMapCords(latlngArray: Coordinate[]) {
  setOrderStore('mapCords', latlngArray);
}

export function setStops(
  topStops: LastUsedTopStopModel[],
  pastStops: LastUsedTopStopModel[],
) {
  setOrderStore(() => ({
    topStops: topStops,
    lastStops: pastStops,
  }));
}

export function setDocuments(documents: DocumentModel[]) {
  setOrderDocumentStore(() => ({
    documents: documents,
    loading: false,
  }));
}

export function setDocumentsLoading(loading: boolean) {
  setOrderDocumentStore('loading', loading);
}

export function setCarrierLoading(loading: boolean) {
  setCarrierStore('loading', loading);
}

export function setPlaylists(data: Playlist[]) {
  setCarrierStore('playlists', data);
}

export {
  carrierStore,
  commodityCodes,
  customerStore,
  ediLogsStore,
  ediSettingsValues,
  eldAssetList,
  equipmentTypes,
  featureFlagGetCustomerFeatures,
  loadSureStore,
  loadTenderStore,
  offerStore,
  orderDocumentStore,
  orderStore,
  postLoadStore,
  resetCarrierStore,
  resetCustomerStore,
  resetFeatureFlagGetCustomerFeatures,
  resetOfferStore,
  resetOrderDocumentStore,
  resetOrderStore,
  setCarrierStore,
  setCommodityCodes,
  setCustomerStore,
  setEdiLogsStore,
  setEdiSettingsValues,
  setEldAssetList,
  setEquipmentTypes,
  setFeatureFlagGetCustomerFeatures,
  setLoadSureStore,
  setLoadTenderStore,
  setOfferStore,
  setOrderDocumentStore,
  setOrderStore,
  setPostLoadStore,
};
